import { ButtonHTMLAttributes, ReactNode, forwardRef } from 'react'
import { clsx } from 'clsx'

export type CtaButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'outline-on-dark'
  | 'ghost'

export type CtaButtonSize = 'sm' | 'md' | 'lg'

interface CtaButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: CtaButtonVariant
  size?: CtaButtonSize
  fullWidth?: boolean
  children: ReactNode
}

const VARIANT_MAP: Record<CtaButtonVariant, string> = {
  primary:
    'bg-qb-primary text-white hover:bg-qb-primary-hover active:bg-qb-primary-pressed',
  secondary:
    'bg-qb-bg-secondary text-qb-text-high hover:bg-qb-gray-100',
  outline:
    'border-[1.5px] border-qb-primary text-qb-primary bg-transparent hover:bg-qb-primary-subtle',
  'outline-on-dark':
    'border-[1.5px] border-white text-white bg-transparent hover:bg-white/10',
  ghost: 'bg-transparent text-qb-text-mid hover:text-qb-text-high',
}

const SIZE_MAP: Record<CtaButtonSize, string> = {
  sm: 'h-10 px-5 text-qb-body-4',
  md: 'h-12 px-6 text-qb-body-3',
  lg: 'h-14 px-8 text-qb-body-2',
}

export const CtaButton = forwardRef<HTMLButtonElement, CtaButtonProps>(
  function CtaButton(
    {
      variant = 'primary',
      size = 'lg',
      fullWidth = false,
      className,
      children,
      disabled,
      type = 'button',
      ...rest
    },
    ref
  ) {
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled}
        className={clsx(
          'inline-flex items-center justify-center font-semibold whitespace-nowrap',
          'rounded-qb-full transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-qb-primary focus-visible:ring-offset-2',
          SIZE_MAP[size],
          VARIANT_MAP[variant],
          fullWidth && 'w-full',
          disabled && 'opacity-50 cursor-not-allowed',
          className
        )}
        {...rest}
      >
        {children}
      </button>
    )
  }
)
